import { useState, useRef, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Loader2, Camera, X, User, Users, CreditCard, Heart, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const childSchema = z.object({
  full_name: z.string().trim().min(2, "الاسم يجب أن يكون حرفين على الأقل").max(100, "الاسم طويل جداً"),
  date_of_birth: z.string().min(1, "يرجى إدخال تاريخ الميلاد"),
  gender: z.string().optional().or(z.literal("")),
  nationality: z.string().trim().max(50).optional().or(z.literal("")),
  national_id: z.string().trim().max(30).optional().or(z.literal("")),
  class_id: z.string().optional().or(z.literal("")),
  branch_id: z.string().optional().or(z.literal("")),
  parent_name: z.string().trim().min(2, "اسم ولي الأمر مطلوب").max(100),
  mother_name: z.string().trim().max(100).optional().or(z.literal("")),
  parent_phone: z.string().trim().max(20, "رقم الهاتف طويل جداً").optional().or(z.literal("")),
  address: z.string().trim().max(300).optional().or(z.literal("")),
  parent_id: z.string().optional().or(z.literal("")),
  enrollment_date: z.string().optional().or(z.literal("")),
  monthly_fee: z.string().optional().or(z.literal("")),
  health_notes: z.string().trim().max(500, "الملاحظات طويلة جداً").optional().or(z.literal("")),
  blood_type: z.string().optional().or(z.literal("")),
  emergency_contact_name: z.string().trim().max(100).optional().or(z.literal("")),
  emergency_contact_phone: z.string().trim().max(20).optional().or(z.literal("")),
  emergency_contact_relation: z.string().trim().max(50).optional().or(z.literal("")),
});

type ChildFormValues = z.infer<typeof childSchema>;

interface ParentAccount {
  user_id: string;
  full_name: string | null;
}

interface AddChildDialogProps {
  onSuccess: () => void;
  classes: { id: string; name: string }[];
  parentAccounts?: ParentAccount[];
}

const steps = [
  { id: 0, title: "بيانات الطفل", icon: User },
  { id: 1, title: "ولي الأمر", icon: Users },
  { id: 2, title: "الاشتراك", icon: CreditCard },
  { id: 3, title: "الصحة والطوارئ", icon: Heart },
];

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const AddChildDialog = ({ onSuccess, classes, parentAccounts = [] }: AddChildDialogProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [branches, setBranches] = useState<{ id: string; name: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { profile } = useAuth();

  // Fetch branches for current nursery
  useEffect(() => {
    if (!profile?.nursery_id) return;
    supabase
      .from("nurseries")
      .select("id, name")
      .eq("parent_nursery_id", profile.nursery_id)
      .then(({ data }) => {
        if (data && data.length > 0) setBranches(data);
      });
  }, [profile?.nursery_id]);

  const form = useForm<ChildFormValues>({
    resolver: zodResolver(childSchema),
    defaultValues: {
      full_name: "", date_of_birth: "", gender: "", nationality: "", national_id: "",
      class_id: "", branch_id: "", parent_name: "", mother_name: "", parent_phone: "", address: "",
      parent_id: "", enrollment_date: new Date().toISOString().split("T")[0],
      monthly_fee: "", health_notes: "", blood_type: "", emergency_contact_name: "",
      emergency_contact_phone: "", emergency_contact_relation: "",
    },
  });

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("يرجى اختيار ملف صورة"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("حجم الصورة يجب أن يكون أقل من 2 ميجابايت"); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadPhoto = async (childId: string): Promise<string | null> => {
    if (!photoFile) return null;
    const ext = photoFile.name.split(".").pop();
    const path = `${profile?.nursery_id}/${childId}.${ext}`;
    const { error } = await supabase.storage.from("child-photos").upload(path, photoFile, { upsert: true });
    if (error) { console.error("Photo upload error:", error); return null; }
    const { data: signedData } = await supabase.storage.from("child-photos").createSignedUrl(path, 60 * 60 * 24 * 365);
    return signedData?.signedUrl || null;
  };

  const validateCurrentStep = async () => {
    const fieldsPerStep: Record<number, (keyof ChildFormValues)[]> = {
      0: ["full_name", "date_of_birth"],
      1: ["parent_name"],
      2: [],
      3: [],
    };
    const fields = fieldsPerStep[currentStep];
    if (fields.length === 0) return true;
    const result = await form.trigger(fields);
    return result;
  };

  const nextStep = async () => {
    const valid = await validateCurrentStep();
    if (valid && currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const onSubmit = async (values: ChildFormValues) => {
    if (!profile?.nursery_id) { toast.error("لم يتم العثور على حضانتك"); return; }
    setLoading(true);
    const { data, error } = await supabase.from("children").insert({
      nursery_id: profile.nursery_id,
      full_name: values.full_name,
      date_of_birth: values.date_of_birth,
      gender: values.gender || null,
      nationality: values.nationality || null,
      national_id: values.national_id || null,
      parent_name: values.parent_name,
      mother_name: values.mother_name || null,
      parent_phone: values.parent_phone || null,
      address: values.address || null,
      class_id: values.class_id || null,
      branch_id: values.branch_id || null,
      health_notes: values.health_notes || null,
      blood_type: values.blood_type || null,
      parent_id: values.parent_id || null,
      enrollment_date: values.enrollment_date || new Date().toISOString().split("T")[0],
      monthly_fee: values.monthly_fee ? parseFloat(values.monthly_fee) : 0,
      emergency_contact_name: values.emergency_contact_name || null,
      emergency_contact_phone: values.emergency_contact_phone || null,
      emergency_contact_relation: values.emergency_contact_relation || null,
    } as any).select("id").single();

    if (error) { setLoading(false); toast.error("خطأ في تسجيل الطفل: " + error.message); return; }

    if (photoFile && data) {
      const photoUrl = await uploadPhoto(data.id);
      if (photoUrl) await supabase.from("children").update({ photo_url: photoUrl }).eq("id", data.id);
    }

    setLoading(false);
    toast.success("تم تسجيل الطفل بنجاح! 🎉");
    form.reset();
    removePhoto();
    setCurrentStep(0);
    setOpen(false);
    onSuccess();
  };

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (!v) { setCurrentStep(0); form.reset(); removePhoto(); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="gradient-hero border-0">
          <Plus className="w-4 h-4 ml-2" />
          تسجيل طفل
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-lg">تسجيل طفل جديد</DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-between gap-1 px-2 pb-2">
          {steps.map((step, i) => {
            const Icon = step.icon;
            const isActive = i === currentStep;
            const isDone = i < currentStep;
            return (
              <div key={step.id} className="flex items-center gap-1 flex-1">
                <button
                  type="button"
                  onClick={() => i < currentStep && setCurrentStep(i)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium transition-all w-full justify-center",
                    isActive && "bg-primary text-primary-foreground shadow-sm",
                    isDone && "bg-primary/10 text-primary cursor-pointer hover:bg-primary/20",
                    !isActive && !isDone && "bg-muted text-muted-foreground"
                  )}
                >
                  {isDone ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">{step.title}</span>
                </button>
                {i < steps.length - 1 && (
                  <div className={cn("h-0.5 w-4 flex-shrink-0 rounded", isDone ? "bg-primary/40" : "bg-border")} />
                )}
              </div>
            );
          })}
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex-1 overflow-y-auto space-y-4 px-1">
            {/* Step 0: Child Info */}
            {currentStep === 0 && (
              <div className="space-y-4 animate-in fade-in-50 duration-200">
                <div className="flex flex-col items-center gap-2">
                  <div
                    className="relative w-20 h-20 rounded-full border-2 border-dashed border-border bg-muted/50 flex items-center justify-center cursor-pointer overflow-hidden hover:border-primary/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {photoPreview ? (
                      <img src={photoPreview} alt="صورة الطفل" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>
                  {photoPreview && (
                    <Button type="button" variant="ghost" size="sm" onClick={removePhoto} className="text-destructive text-xs h-6">
                      <X className="w-3 h-3 ml-1" /> إزالة
                    </Button>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                  {!photoPreview && <span className="text-xs text-muted-foreground">صورة الطفل (اختياري)</span>}
                </div>

                <FormField control={form.control} name="full_name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>اسم الطفل الكامل *</FormLabel>
                    <FormControl><Input placeholder="مثال: أحمد محمد علي" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="date_of_birth" render={({ field }) => (
                    <FormItem>
                      <FormLabel>تاريخ الميلاد *</FormLabel>
                      <FormControl><Input type="date" dir="ltr" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="gender" render={({ field }) => (
                    <FormItem>
                      <FormLabel>الجنس</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="اختر" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="male">ذكر 👦</SelectItem>
                          <SelectItem value="female">أنثى 👧</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="nationality" render={({ field }) => (
                    <FormItem>
                      <FormLabel>الجنسية</FormLabel>
                      <FormControl><Input placeholder="مصري" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="national_id" render={({ field }) => (
                    <FormItem>
                      <FormLabel>رقم الهوية</FormLabel>
                      <FormControl><Input placeholder="رقم الهوية الوطنية" dir="ltr" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <FormField control={form.control} name="class_id" render={({ field }) => (
                  <FormItem>
                    <FormLabel>الفصل</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="اختر الفصل (اختياري)" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {classes.map((cls) => (
                          <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />

                {branches.length > 0 && (
                  <FormField control={form.control} name="branch_id" render={({ field }) => (
                    <FormItem>
                      <FormLabel>الفرع</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="اختر الفرع (اختياري)" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {branches.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}
              </div>
            )}

            {/* Step 1: Parent Info */}
            {currentStep === 1 && (
              <div className="space-y-4 animate-in fade-in-50 duration-200">
                <FormField control={form.control} name="parent_name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>اسم ولي الأمر (الأب) *</FormLabel>
                    <FormControl><Input placeholder="أدخل اسم ولي الأمر" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="mother_name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>اسم الأم</FormLabel>
                    <FormControl><Input placeholder="أدخل اسم الأم" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="parent_phone" render={({ field }) => (
                  <FormItem>
                    <FormLabel>هاتف ولي الأمر</FormLabel>
                    <FormControl><Input placeholder="05xxxxxxxx" dir="ltr" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="address" render={({ field }) => (
                  <FormItem>
                    <FormLabel>العنوان</FormLabel>
                    <FormControl><Input placeholder="المدينة - الحي - الشارع" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                {parentAccounts.length > 0 && (
                  <FormField control={form.control} name="parent_id" render={({ field }) => (
                    <FormItem>
                      <FormLabel>ربط بحساب ولي أمر</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="اختر حساب ولي الأمر (اختياري)" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {parentAccounts.map((p) => (
                            <SelectItem key={p.user_id} value={p.user_id}>{p.full_name || "بدون اسم"}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}
              </div>
            )}

            {/* Step 2: Subscription */}
            {currentStep === 2 && (
              <div className="space-y-4 animate-in fade-in-50 duration-200">
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-1">
                  <p className="text-sm font-medium">💡 معلومات الاشتراك</p>
                  <p className="text-xs text-muted-foreground">حدد تاريخ التسجيل والاشتراك الشهري. إذا تم التسجيل بعد يوم 1 من الشهر، سيتم حساب فاتورة جزئية تلقائياً.</p>
                </div>
                <FormField control={form.control} name="enrollment_date" render={({ field }) => (
                  <FormItem>
                    <FormLabel>تاريخ التسجيل</FormLabel>
                    <FormControl><Input type="date" dir="ltr" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="monthly_fee" render={({ field }) => (
                  <FormItem>
                    <FormLabel>الاشتراك الشهري (ج.م)</FormLabel>
                    <FormControl><Input type="number" min={0} step={0.01} dir="ltr" placeholder="0" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            )}

            {/* Step 3: Health & Emergency */}
            {currentStep === 3 && (
              <div className="space-y-4 animate-in fade-in-50 duration-200">
                <FormField control={form.control} name="blood_type" render={({ field }) => (
                  <FormItem>
                    <FormLabel>فصيلة الدم</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="اختر فصيلة الدم" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {BLOOD_TYPES.map((bt) => (
                          <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="health_notes" render={({ field }) => (
                  <FormItem>
                    <FormLabel>ملاحظات صحية</FormLabel>
                    <FormControl><Textarea placeholder="حساسية، أمراض مزمنة، أدوية..." className="resize-none" rows={3} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <div className="rounded-lg border border-border bg-destructive/5 p-3 space-y-3">
                  <p className="text-sm font-medium text-destructive">🚨 جهة اتصال الطوارئ</p>
                  <FormField control={form.control} name="emergency_contact_name" render={({ field }) => (
                    <FormItem>
                      <FormLabel>الاسم</FormLabel>
                      <FormControl><Input placeholder="اسم الشخص" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="emergency_contact_phone" render={({ field }) => (
                      <FormItem>
                        <FormLabel>الهاتف</FormLabel>
                        <FormControl><Input placeholder="05xxxxxxxx" dir="ltr" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="emergency_contact_relation" render={({ field }) => (
                      <FormItem>
                        <FormLabel>صلة القرابة</FormLabel>
                        <FormControl><Input placeholder="عم، خال، جد..." {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center gap-3 pt-3 pb-1 sticky bottom-0 bg-background">
              {currentStep > 0 && (
                <Button type="button" variant="outline" onClick={prevStep} className="gap-1">
                  <ChevronRight className="w-4 h-4" />
                  السابق
                </Button>
              )}
              <div className="flex-1" />
              {currentStep < steps.length - 1 ? (
                <Button type="button" onClick={nextStep} className="gradient-hero border-0 gap-1">
                  التالي
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              ) : (
                <Button type="submit" className="gradient-hero border-0 gap-1" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> تسجيل الطفل</>}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddChildDialog;
